import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { VariableSizeList as VirtualList } from 'react-window';
import { useComponentWebSocket } from './GlobalSettingUtils';

const LOG_ROW_VERTICAL_PADDING = 16;
const LOG_ROW_HORIZONTAL_PADDING = 16;
const LOG_METADATA_WIDTH = 122;
const LOG_MESSAGE_LINE_HEIGHT = 18;
const LOG_MESSAGE_FONT_SIZE = '0.72rem';
const LOG_MESSAGE_MAX_HEIGHT = 320;

// Memoized log item component for better performance
const LogItem = React.memo(({ index, style, data }) => {
  const { logs, getLevelColor } = data;
  const log = logs[index];
  
  if (!log) return null;

  return (
    <div style={style}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: '1px solid #f0f0f0',
          fontSize: '0.75rem',
          textAlign: 'left',
          minHeight: 'auto',
          boxSizing: 'border-box'
        }}
      >
        <Box
          sx={{
            width: LOG_METADATA_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'flex-start'
          }}
        >
          <Chip
            label={log.level}
            size="small"
            sx={{
              backgroundColor: getLevelColor(log.level),
              color: 'white',
              fontWeight: 'bold',
              minWidth: 58,
              maxWidth: 74,
              height: 20,
              fontSize: '0.65rem',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.68rem',
              whiteSpace: 'nowrap',
              textAlign: 'left'
            }}
          >
            {log.timestamp ? log.timestamp.slice(-8) : ''}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            pr: 0.5
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: LOG_MESSAGE_FONT_SIZE,
              lineHeight: `${LOG_MESSAGE_LINE_HEIGHT}px`,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              display: 'block',
              margin: 0,
              px: 1,
              py: 0.75,
              maxHeight: LOG_MESSAGE_MAX_HEIGHT,
              overflow: 'auto',
              backgroundColor: '#fafafa',
              border: '1px solid #eeeeee',
              borderRadius: 1
            }}
          >
            {log.message}
          </Typography>
        </Box>
      </Box>
    </div>
  );
});

LogItem.displayName = 'LogItem';

const GlobalSettingLogs = forwardRef((props, ref) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [availableLogs, setAvailableLogs] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState('all');
  const [listWidth, setListWidth] = useState(900);
  const listRef = React.useRef(null);
  const listContainerRef = React.useRef(null);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // WebSocket command handlers
  const commandHandlers = {
    'get_logs_response': (message) => {
      console.log('📥 Received logs response:', message);
      
      // Extract data object - WebSocket sends {command: "...", data: {...}}
      const responseData = message.data || message;
      
      // Backend returns {status: "success", logs: [...], total_logs: N, available_logs: [...]} or {status: "error", message: "...", logs: []}
      if (responseData.status === 'error') {
        console.error('Error fetching logs:', responseData.message);
        setLogs([]);
        setLoading(false);
        return;
      }
      
      // Update available log files for dropdown
      if (responseData.available_logs && Array.isArray(responseData.available_logs)) {
        setAvailableLogs(responseData.available_logs);
      }
      
      // Backend now returns simple string array, convert to objects for frontend use
      const logStrings = responseData.logs || [];
      console.log('📊 Log strings received:', logStrings.length, 'lines');
      
      const processedLogs = logStrings.map((logLine, index) => {
        // Try to extract timestamp - handle both prefixed [SERVICE] and non-prefixed formats
        // Match: "[SERVICE] 2025-11-30 12:32:59" or "2025-11-30 12:32:59"
        const timestampMatch = logLine.match(/(?:\[.*?\]\s+)?(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/);
        const timestamp = timestampMatch ? timestampMatch[1] : '';
        
        // Simple level detection
        let level = 'INFO';
        const lowerLine = logLine.toLowerCase();
        if (lowerLine.includes('error') || lowerLine.includes('exception') || lowerLine.includes('failed')) {
          level = 'ERROR';
        } else if (lowerLine.includes('warning') || lowerLine.includes('warn')) {
          level = 'WARNING';
        } else if (lowerLine.includes('debug')) {
          level = 'DEBUG';
        }
        
        return {
          id: index,
          timestamp: timestamp,
          level: level,
          message: logLine,
          rawLine: logLine
        };
      });
      
      console.log('✅ Processed logs:', processedLogs.length, 'items');
      console.log('📋 First few logs:', processedLogs.slice(0, 3));
      
      setLogs(processedLogs);
      setLoading(false);
      setLastRefresh(new Date());
      
      // Scroll to bottom after logs are loaded
      setTimeout(() => {
        if (listRef.current && processedLogs.length > 0) {
          listRef.current.scrollToItem(processedLogs.length - 1, 'end');
        }
      }, 100);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    refreshData: fetchLogs,
    isModified: () => false,
    saveChanges: () => Promise.resolve(true)
  }));  // Fetch logs from server
  const fetchLogs = useCallback(() => {
    setLoading(true);
    sendCommand('get_logs', { limit: 1000, log_file: selectedLogFile });
  }, [selectedLogFile, sendCommand]);

  // Memoized filtered logs for better performance
  const filteredLogs = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    
    let filtered = logs;

    if (debouncedSearchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.level.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level.toLowerCase() === levelFilter);
    }

    return filtered;
  }, [logs, debouncedSearchTerm, levelFilter]);

  // Download logs as text file
  const downloadLogs = useCallback(() => {
    const logsToDownload = filteredLogs.length > 0 ? filteredLogs : logs;
    if (!Array.isArray(logsToDownload) || logsToDownload.length === 0) {
      return; // Don't download if no logs
    }
    
    const logText = logsToDownload.map(log => 
      `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, filteredLogs]);

  // Get log level color - memoized
  const getLevelColor = useCallback((level) => {
    switch (level.toLowerCase()) {
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      case 'debug': return '#9e9e9e';
      default: return '#757575';
    }
  }, []);
  // Memoized data for virtual list
  const virtualListData = useMemo(() => ({
    logs: filteredLogs,
    getLevelColor
  }), [filteredLogs, getLevelColor]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.resetAfterIndex(0, true);
  }, [filteredLogs, listWidth]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = new ResizeObserver(([entry]) => {
      setListWidth(Math.max(320, Math.floor(entry.contentRect.width)));
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate item height based on content
  const getItemSize = useCallback((index) => {
    const log = filteredLogs[index];
    if (!log) return 50;
    
    const messageWidth = Math.max(
      220,
      listWidth - LOG_METADATA_WIDTH - LOG_ROW_HORIZONTAL_PADDING - 16
    );
    const averageCharacterWidth = 7.2;
    const charactersPerLine = Math.max(24, Math.floor(messageWidth / averageCharacterWidth));
    const visualLines = log.message.split('\n').reduce((lineCount, segment) => {
      return lineCount + Math.max(1, Math.ceil(segment.length / charactersPerLine));
    }, 0);
    const textHeight = Math.min(
      LOG_MESSAGE_MAX_HEIGHT,
      visualLines * LOG_MESSAGE_LINE_HEIGHT + 14
    );
    
    return Math.max(58, textHeight + LOG_ROW_VERTICAL_PADDING + 2);
  }, [filteredLogs, listWidth]);
  // Auto-refresh functionality - DISABLED
  // useEffect(() => {
  //   if (autoRefresh) {
  //     autoRefreshInterval.current = setInterval(fetchLogs, 5000); // Refresh every 5 seconds
  //   } else {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   }

  //   return () => {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   };
  // }, [autoRefresh]);
  // Initial load
  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#5757b0' }}>
        System Logs
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        View and manage application logs. Use filters to find specific log entries.
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            label="Search logs"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Log file selector */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Log File</InputLabel>
            <Select
              value={selectedLogFile}
              label="Log File"
              onChange={(e) => {
                setSelectedLogFile(e.target.value);
                // Auto-refresh when changing log file
                setTimeout(() => {
                  setLoading(true);
                  sendCommand('get_logs', { limit: 1000, log_file: e.target.value });
                }, 100);
              }}
            >
              <MenuItem value="all">All Services (Aggregated)</MenuItem>
              {availableLogs.map((logFile) => (
                <MenuItem key={logFile} value={logFile}>
                  {logFile.replace('_output.log', '').replace('_error.log', ' (errors)').replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Level filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={levelFilter}
              label="Level"
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Tooltip title="Refresh logs">
              <IconButton 
                onClick={fetchLogs}
                disabled={loading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>            <Tooltip title="Download logs">
              <IconButton 
                onClick={downloadLogs}
                disabled={logs.length === 0}
                color="primary"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${Array.isArray(logs) ? logs.length : 0}`} 
            size="small" 
            color="default"
          />
          <Chip 
            label={`Showing: ${Array.isArray(filteredLogs) ? filteredLogs.length : 0}`} 
            size="small" 
            color="primary"
          />
          <Chip 
            label={`Source: ${selectedLogFile === 'all' ? 'All Services' : selectedLogFile}`} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
          {lastRefresh && (
            <Chip 
              label={`Last refresh: ${formatTimestamp(lastRefresh)}`} 
              size="small" 
              color="default"
              variant="outlined"
            />
          )}
        </Box>{/* Auto-refresh toggle - DISABLED */}
        {/* <Box sx={{ mt: 2 }}>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            color="primary"
          >
            {autoRefresh ? 'Stop Auto-refresh' : 'Start Auto-refresh'}
          </Button>
        </Box> */}
      </Paper>      {/* Logs display */}
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading logs...
        </Alert>
      )}      {(!Array.isArray(filteredLogs) || filteredLogs.length === 0) && !loading ? (
        <Alert severity="info">
          No logs found. {debouncedSearchTerm || levelFilter !== 'all' ? 'Try adjusting your filters.' : 'System logs will appear here.'}
        </Alert>
      ) : (        <Paper ref={listContainerRef} sx={{ height: 600, overflow: 'hidden' }}>
          <VirtualList
            ref={listRef}
            height={600}
            width="100%"
            itemCount={filteredLogs.length}
            itemSize={getItemSize}
            itemData={virtualListData}
            overscanCount={5}
          >
            {LogItem}
          </VirtualList>
        </Paper>
      )}
    </Box>
  );
});

GlobalSettingLogs.displayName = 'GlobalSettingLogs';

export default GlobalSettingLogs;
